import { makeStyles, tokens, Text } from '@coreai/fluentui-react';
import { useAppStore } from '@/stores/appStore';
import { useCurrentApp } from '@/hooks/useCurrentApp';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AppOverviewHeader } from '@/components/shared/AppOverviewHeader';
import { PageSection } from '@/components/shared/PageSection';
import {
  ParametersFooterActions,
  ParametersView,
} from '@/components/ParametersList/ParametersView';
import { useParametersEditor } from '@/components/ParametersList/useParametersEditor';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  footer: {
    paddingTop: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * ParametersPage — full-page wrapper around `ParametersView`. Mirrors
 * `ParametersDialog` so the route and the modal stay behaviorally identical.
 *
 * Renders the standard app shell (`AppOverviewHeader` + `PageSection`) for
 * consistency with the other app-scoped pages.
 */
export const ParametersPage = () => {
  const styles = useStyles();
  const { app, appName, locationName } = useCurrentApp();
  const { projectName: routeProjectName } = useCurrentProject();
  const selectedApp = useAppStore((s) => s.selectedApp);
  // Parameters editor was historically wired through the store-based
  // selection. Keep that wiring intact so we don't perturb the editor
  // subsystem, but power the page chrome from URL-derived context.
  // The URL is the single source of truth for the project so a stale
  // Zustand selection can't trick the editor into fetching/saving the
  // wrong project.
  const editorProjectName = routeProjectName ?? '';
  const selectedMatchesRoute =
    !!selectedApp?.name && !!appName && selectedApp.name.toLowerCase() === appName.toLowerCase();
  const routeMatchedApp = selectedMatchesRoute ? selectedApp : undefined;
  const editorAppName = appName ?? routeMatchedApp?.name ?? '';
  const editorAppId = app?.properties.appResourceId ?? routeMatchedApp?.properties.appResourceId ?? '';

  useDocumentTitle(['Parameters', editorAppName, editorProjectName]);

  const { data: workflows } = useAppWorkflows(app ?? null);
  const state = useParametersEditor({
    projectName: editorProjectName,
    appName: editorAppName,
    appId: editorAppId,
    enabled: !!editorAppName && !!editorAppId,
  });

  const headerName = editorAppName || appName || '';

  if (!headerName) {
    return (
      <div className={styles.page}>
        <PageSection title="Parameters">
          <div className={styles.empty}>
            <Text>Select an application to view parameters.</Text>
          </div>
        </PageSection>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <AppOverviewHeader
        name={headerName}
        app={app}
        region={locationName}
        workflowCount={workflows?.length ?? 0}
      />
      <PageSection title="Parameters">
        <div className={styles.body}>
          <ParametersView state={state} />
        </div>
        <div className={styles.footer}>
          <ParametersFooterActions state={state} />
        </div>
      </PageSection>
    </div>
  );
};

ParametersPage.displayName = 'ParametersPage';
